import { isCovered } from '@/config/constants';
import { useCurrentCountry } from 'utils/hooks';
import { useLocation } from 'react-router-dom';
import { DATA_SOURCES as SOURCE_META } from 'utils/metrics';
import { Breadcrumbs } from './Breadcrumbs/index';
import Link from './Link';
import NoDataSection from './NoDataSection';
import { PlaceholderText } from './Placeholders';
import SerialComma from './SerialComma';
import { mixpanelTrack } from 'utils/app';
import { useFetchUserAbout } from 'utils/hooks/user';
import { views } from '@/copy';
import useEntityAcrossSources from 'utils/hooks/useEntityAcrossSources';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';
import usePresenceAcrossSources, {
    SOURCE_ORDER,
} from 'utils/hooks/usePresenceAcrossSources';
import { useEffect } from 'react';

/**
 * Why a page is empty. No plan or entitlement reason belongs here — see
 * frontend/CONTRIBUTING.md, "No-data copy must not name a cause".
 */
export const NO_DATA_REASONS = {
    /** This source does not cover this industry/market at all. */
    NO_COVERAGE: 'no-coverage',
    /** Covered, but this source's taxonomy has no such entity. */
    NOT_IN_SOURCE: 'not-in-source',
    /** Covered, present, and still empty — a real gap in our data. */
    NO_DATA: 'no-data',
};

/**
 * `NoGoogleData`, `NoInstagramData`, and so on. Data ops keys its dashboards on
 * these names, and `NoDataSourceMarketCoverage` still emits them elsewhere.
 */
const mixpanelEvent = (label) => `No${label}Data`;

/** The tab each source lives on; the pop index is the base path, with no segment. */
const ROUTE_SEGMENT = {
    POPULARITY: '',
    GOOGLE: 'google',
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
};

/** Most specific first: `NOT_IN_SOURCE` is the more honest answer than `NO_DATA`. */
const getReason = ({ isEntityCovered, isInSourceTaxonomy }) => {
    if (!isEntityCovered) return NO_DATA_REASONS.NO_COVERAGE;
    if (!isInSourceTaxonomy) return NO_DATA_REASONS.NOT_IN_SOURCE;
    return NO_DATA_REASONS.NO_DATA;
};

/**
 * The real breadcrumb pills, minus the links and menus — same primitives,
 * numbering and slot count, so this reads as the page it would have been.
 * Nothing is navigable, because none of it has data to show.
 */
function BreadcrumbContext({ crumbs, slots }) {
    if (!crumbs.length) return null;
    return (
        <Breadcrumbs.Container>
            {crumbs.map((crumb, index) => (
                <Breadcrumbs.Breadcrumb className="capitalize" key={crumb}>
                    <span className="truncate">{`${index + 1}. ${crumb}`}</span>
                </Breadcrumbs.Breadcrumb>
            ))}
            {Array.from({ length: Math.max(0, slots - crumbs.length) }).map(
                (_empty, index) => (
                    <Breadcrumbs.Breadcrumb key={`empty-crumb-${index}`} />
                ),
            )}
        </Breadcrumbs.Container>
    );
}

/**
 * What a data-source tab renders instead of an empty layout.
 *
 * The tab rendering this is by definition the one that could not resolve the
 * entity, so the name and hierarchy come from `useEntityAcrossSources`, and
 * where it *does* have data comes from `usePresenceAcrossSources`. Those are two
 * questions: one taxonomy knowing an id says nothing about another source having
 * measurements for it, which is the whole subject of this screen.
 *
 * `showIndustriesMenu` and `maxDepth` mirror BreadCrumbs. `tabs` must keep
 * rendering — this screen stands in for the whole view, and they are the way out.
 */
function EntityNoData({
    entityId,
    entityType = 'categories',
    dataSource,
    showIndustriesMenu = false,
    maxDepth = 5,
    tabs = null,
}) {
    const country = useCurrentCountry();
    const { pathname } = useLocation();
    const aboutQuery = useFetchUserAbout();
    const allowedCountries = aboutQuery.data?.allowed_countries;
    const getHierarchy = useGetHierarchy();
    const { entity, isLoading, knownSources } = useEntityAcrossSources({
        id: entityId,
        entityType,
        dataSource,
    });
    const { presence, isLoading: presenceLoading } = usePresenceAcrossSources({
        id: entityId,
        entityType,
    });

    const { label } = SOURCE_META[dataSource] || {};
    // The id, not the record: a record is rebuilt whenever its query is, and a
    // re-filed event is a no-data view that never happened.
    const market = country?.text_id;
    const name = entity?.name;
    const { hierarchy_hint } = getHierarchy(entity?.hierarchies);
    const cat1 = hierarchy_hint?.cat1;

    // NO_COVERAGE is the one claim about us rather than the data, so it needs a
    // known cat1. Brands have none, so their coverage is the market alone.
    const knowsCoverage = entityType === 'brands' || !!cat1;
    const isEntityCovered =
        !knowsCoverage ||
        (entityType === 'brands'
            ? !!SOURCE_META[dataSource]?.supportedGeos?.includes(market)
            : isCovered({ geo: market, dataSource, cat1 }));

    // Only claim the source lacks the entity when some source could answer.
    const isInSourceTaxonomy =
        !knownSources.length || knownSources.includes(dataSource);

    const reason = getReason({ isEntityCovered, isInSourceTaxonomy });

    // An unknown source would file `NoundefinedData`, so it waits with the rest.
    const isReady = !isLoading && !!market && !!label;
    useEffect(() => {
        if (!isReady) return;
        mixpanelTrack(mixpanelEvent(label), {
            reason,
            dataSource: label,
            entityType,
            entityId,
            entityName: name,
            cat1,
            country: market,
        });
    }, [isReady, reason, label, entityType, entityId, name, cat1, market]);

    // Brand pages carry no breadcrumb bar. Categories keep the entity as the
    // last crumb, since the heading no longer names it.
    const crumbs =
        entityType === 'brands'
            ? []
            : [
                  ...(showIndustriesMenu ? [hierarchy_hint?.cat1] : []),
                  hierarchy_hint?.cat2,
                  hierarchy_hint?.cat3,
                  hierarchy_hint?.cat4,
                  hierarchy_hint?.cat5,
                  name,
              ].filter(
                  (crumb, index, all) => crumb && all.indexOf(crumb) === index,
              );

    // One slot per level the real bar would show, so the row keeps its width.
    const slots = maxDepth - (showIndustriesMenu ? 0 : 1);

    // Where the entity does have data. A hint is a promise that somewhere is
    // worth going, so a source is only offered for a market it covers the entity
    // in -- the same test this screen just applied to itself.
    const coversThere = (geo, source) =>
        entityType === 'brands'
            ? !!SOURCE_META[source]?.supportedGeos?.includes(geo)
            : !cat1 || isCovered({ geo, dataSource: source, cat1 });

    const otherSources = market
        ? SOURCE_ORDER.filter(
              (source) =>
                  source !== dataSource &&
                  presence[source]?.includes(market) &&
                  coversThere(market, source),
          )
        : [];
    // Only markets this account can open. Offering one it cannot gets the user
    // bounced by MaybeRedirectGeo into an upsell modal, which is worse than
    // having said nothing.
    const otherMarkets = (presence[dataSource] || []).filter(
        (geo) =>
            geo !== market &&
            coversThere(geo, dataSource) &&
            allowedCountries?.includes(geo),
    );

    // A source's own tab is one click away, but say it with a link anyway: the
    // sentence names the source, so the name is what a reader aims at.
    //
    // Neither destination is passed through `mutateToWithCountryParam` -- `Link`
    // already does that, and doing it twice loses the market being offered: the
    // first pass drops a country that is the user's default, and the second
    // reads that absence as "keep the current one".
    const sourceBasePath = pathname.replace(
        /\/(google|tiktok|instagram)\/?$/,
        '',
    );
    const pathForSource = (source) => {
        const segment = ROUTE_SEGMENT[source];
        return segment ? `${sourceBasePath}/${segment}` : sourceBasePath;
    };
    const sources = otherSources.length ? (
        <SerialComma>
            {otherSources.map((source) => (
                <Link key={source} to={pathForSource(source)}>
                    {SOURCE_META[source]?.label}
                </Link>
            ))}
        </SerialComma>
    ) : null;
    // Same page, same source, other market -- so the path has to be named, or
    // the link resolves against the site root instead of staying put.
    const markets = otherMarkets.length ? (
        <SerialComma>
            {otherMarkets.map((geo) => (
                <Link key={geo} to={{ pathname, search: `?country=${geo}` }}>
                    {geo === 'GLOBAL' ? 'Global' : geo}
                </Link>
            ))}
        </SerialComma>
    ) : null;

    const subject = name ? (
        <span className="capitalize">{name}</span>
    ) : (
        'this page'
    );
    const { entityNoData } = views;
    // The explanation is true without presence, so it renders straight away and
    // nothing waits on four data stores. Only the closing sentence waits, and it
    // holds its place while it does -- for entitlements too, since they decide
    // which markets may be offered. The reader sees a sentence coming rather
    // than one that arrives and then changes its mind.
    const advice =
        presenceLoading || aboutQuery.isLoading ? (
            <PlaceholderText width="14rem" />
        ) : sources || markets ? (
            entityNoData.availability({ sources, markets })
        ) : (
            entityNoData.tryAnotherSource
        );
    const message =
        reason === NO_DATA_REASONS.NO_COVERAGE
            ? entityNoData.noCoverage({
                  subject,
                  source: label,
                  country,
                  advice,
              })
            : entityNoData.noData({ subject, source: label, country, advice });

    return (
        <>
            <BreadcrumbContext crumbs={crumbs} slots={slots} />
            {tabs}
            <NoDataSection
                heading={entityNoData.heading}
                message={
                    <span className="mx-auto mt-2 block max-w-prose text-pretty">
                        {message}
                    </span>
                }
            />
        </>
    );
}

export default EntityNoData;
